import { useRef } from 'react';
import { Volume2, VolumeX, Play, Pause, RotateCcw, SkipForward } from 'lucide-react';
import { Progress } from '../ui/progress';
import { TickRing } from '../timer/TickRing';
import { RoundButton } from '../timer/RoundButton';

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const phaseLabel = ({ isTransition, isRest, isSetRest, currentExercise, totalExercises }) => {
  if (isTransition) return 'Transition to Next Exercise';
  if (isRest) return 'Rest Before Next Exercise';
  if (isSetRest) return 'Rest Between Sets';
  return `Exercise ${currentExercise + 1}/${totalExercises}`;
};

const SetIndicator = ({ totalSets, currentSet, isSetRest }) => (
  <div className="flex justify-center gap-2 mb-4">
    {Array.from({ length: totalSets }, (_, i) => (
      <div
        key={i}
        className={`w-9 h-9 flex items-center justify-center border-2 rounded-full font-heading text-base transition-all ${
          i + 1 === currentSet && !isSetRest
            ? 'bg-primary border-primary text-primary-foreground'
            : i + 1 < currentSet
              ? 'bg-primary/30 border-primary/50 text-primary'
              : 'border-white/20 text-white/40'
        }`}
      >
        {i + 1}
      </div>
    ))}
  </div>
);

// Tracks the starting length of the current phase so the ring can empty proportionally.
const usePhaseTotal = (phaseKey, timeLeft) => {
  const ref = useRef({ key: null, total: 0 });
  if (ref.current.key !== phaseKey || timeLeft > ref.current.total) {
    ref.current = { key: phaseKey, total: Math.max(timeLeft, 1) };
  }
  return ref.current.total;
};

const centerText = ({ isCountdown, isTransition, isRest, isSetRest, currentEx, currentSet }) => {
  if (isCountdown) return { top: 'Starting', bottom: 'Get Ready!' };
  if (isTransition) return { top: 'Rest', bottom: `Next: ${currentEx?.name || ''}` };
  if (isRest) return { top: 'Rest', bottom: `Next: ${currentEx?.name || ''}` };
  if (isSetRest) return { top: `Set ${currentSet} complete`, bottom: 'Rest' };
  return { top: currentEx?.sets > 1 ? `Round ${currentSet} of ${currentEx.sets}` : 'Work', bottom: currentEx?.name };
};

export const WorkoutTimer = (props) => {
  const {
    currentEx, currentExercise, totalExercises, currentSet, isRest, isSetRest, isTransition,
    isRunning, isCountdown, countdownTime, timeLeft, totalElapsed, progress,
    soundEnabled, onToggleSound, onReset, onPlayPause, onSkip, onAddTime,
  } = props;
  const resting = isRest || isSetRest || isTransition;
  const phaseKey = `${isCountdown}-${isRest}-${isSetRest}-${isTransition}-${currentExercise}-${currentSet}`;
  const shown = isCountdown ? countdownTime : timeLeft;
  const total = usePhaseTotal(phaseKey, isCountdown ? 5 : timeLeft);
  const { top, bottom } = centerText({ isCountdown, isTransition, isRest, isSetRest, currentEx, currentSet });
  const active = isRunning || isCountdown;

  return (
    <div className="bg-card border border-border rounded-3xl p-5 sm:p-8 mb-6" data-testid="workout-timer">
      <div className="flex items-center justify-between mb-4">
        <span className="text-muted-foreground uppercase tracking-wider text-xs sm:text-sm" data-testid="timer-phase-label">
          {phaseLabel({ isTransition, isRest, isSetRest, currentExercise, totalExercises })}
        </span>
        <button onClick={onToggleSound} className="text-muted-foreground hover:text-white transition-colors" data-testid="timer-sound-toggle" aria-label="Toggle sound">
          {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
        </button>
      </div>

      {currentEx?.sets > 1 && !isRest && !isTransition && (
        <SetIndicator totalSets={currentEx.sets} currentSet={currentSet} isSetRest={isSetRest} />
      )}

      <TickRing progress={Math.min(1, shown / total)} paused={!active && totalElapsed > 0}>
        <span className="text-xs sm:text-sm text-white/50 uppercase tracking-[0.18em] px-6 text-center" data-testid="timer-ring-top">{top}</span>
        <span
          className={`tabular-nums font-bold leading-none my-2 text-[44px] sm:text-[56px] ${isCountdown ? 'text-primary animate-pulse' : resting ? 'text-primary' : 'text-white'}`}
          data-testid="timer-display"
        >
          {isCountdown ? countdownTime : formatTime(timeLeft)}
        </span>
        <span className="font-heading text-sm sm:text-base text-white uppercase tracking-wider px-8 text-center line-clamp-2" data-testid="timer-ring-bottom">{bottom}</span>
      </TickRing>

      <div className="mt-6 mb-6">
        <div className="flex justify-between text-[10px] uppercase tracking-[0.18em] text-white/40 mb-1.5">
          <span>Workout progress</span><span>{Math.round(progress)}%</span>
        </div>
        <Progress value={progress} className="h-1.5 bg-black" />
      </div>

      <div className="flex items-center justify-center gap-3 sm:gap-5">
        <RoundButton onClick={onReset} aria-label="Reset" data-testid="reset-btn"><RotateCcw className="h-5 w-5 mx-auto" /></RoundButton>
        <RoundButton
          variant={active ? 'danger' : 'primary'}
          onClick={() => onPlayPause({ isCountdown, isRunning, totalElapsed })}
          aria-label={active ? 'Pause' : 'Start'}
          data-testid="play-pause-btn"
          className="h-[84px] w-[84px]"
        >
          {active ? <Pause className="h-7 w-7 mx-auto" /> : <Play className="h-7 w-7 mx-auto translate-x-0.5" />}
        </RoundButton>
        <RoundButton onClick={onSkip} aria-label="Skip" data-testid="skip-btn"><SkipForward className="h-5 w-5 mx-auto" /></RoundButton>
      </div>
      {resting && !isCountdown && (
        <div className="flex justify-center mt-4">
          <button
            type="button" onClick={onAddTime}
            className="px-5 py-2 rounded-full bg-primary/15 border border-primary/40 text-primary text-sm font-semibold tabular-nums hover:bg-primary/25 active:scale-95 transition-[background-color,transform]"
            data-testid="add-rest-time-btn"
          >
            +15s rest
          </button>
        </div>
      )}
    </div>
  );
};

export default WorkoutTimer;
